import React, {
  KeyboardEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useURLParamsGetter } from "~/hooks/useURLParamsSetter";
import {
  CameraCheck,
  CameraCheckStatus,
  useAddNotificationMutation,
  useGetCameraCheckByIdLazyQuery,
  useSetCameraCheckStatusMutation,
  useSetNewCameraChatMessageMutation,
} from "~/schemas/generated/graphql";
import { get } from "lodash";
import s from "../CameraZonesView.less";
import TextArea from "~/shared/ui/Fields/TextArea";
import { useForm } from "react-hook-form/dist/index.ie11";
import Preloader from "~/components/Preloader";
import { getFormatDate } from "~/libs/getFormatDate";
import { getShortUserName } from "~/libs/getData";
import { useCurrentUserInfo } from "~/hooks/useCurrentUserInfo";
import clsx from "clsx";
import Button from "~/shared/ui/Fields/Button";
import Add from "~/components/Icons/icon-add";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { NOTIFICATIONS } from "~/constans/notifications";
import { TFunction } from "i18next";
import { CameraStatusUpdateContext } from "~/pages/CameraZonesView/modals/CameraViewModal";
import { CamerasContext } from "~/pages/CameraZonesView";

const validation = (t: TFunction) =>
  yup.object().shape({
    message: yup
      .string()
      .max(250, t("validation.more250"))
      .nullable()
      .required(t("validation.required")),
  });

interface IChatForm {
  message: string;
}

interface IParameters {
  dialog?: string;
}

interface ICameraCheckChatProps {
  toggleModeHandle: () => void;
}

const CameraCheckChat = ({ toggleModeHandle }: ICameraCheckChatProps) => {
  const { t } = useTranslation();
  const { dialog } = useURLParamsGetter<IParameters>();
  const [profile, profileLoading] = useCurrentUserInfo();
  const { reFetchAllDependencyData } = useContext(CameraStatusUpdateContext);
  const { userCanManageCamera } = useContext(CamerasContext);
  // Form
  const {
    control,
    handleSubmit,
    errors,
    setValue,
    formState: { isSubmitting },
  } = useForm<IChatForm>({
    resolver: yupResolver(validation(t)),
  });

  // Form Ref
  const formRef = useRef<HTMLFormElement>(null);

  // State
  const [chatData, setChatData] = useState<CameraCheck>();

  // Req
  const [getCameraCheck, getCameraCheckState] = useGetCameraCheckByIdLazyQuery({
    fetchPolicy: "no-cache",
  });
  const getDialog = () => getCameraCheck({ variables: { id: dialog } });

  // Mutations
  const [addNotificator] = useAddNotificationMutation();
  const [sendMessage, sendMessageState] = useSetNewCameraChatMessageMutation({
    onCompleted() {
      addNotificator({
        variables: {
          type: NOTIFICATIONS.success,
          content: "Комментарий успешно добавлен",
        },
      });
      setValue("message", "");
      getDialog();
    },
  });
  const [setCameraCheckStatus, setCameraCheckStatusState] =
    useSetCameraCheckStatusMutation({
      onCompleted() {
        addNotificator({
          variables: {
            type: NOTIFICATIONS.success,
            content: 'Статус замечания изменен как "Согласовано"',
          },
        });

        reFetchAllDependencyData();
        toggleModeHandle();
      },
    });

  // Handlers
  const setStatusHandler = (dialog: string) => () => {
    const variables = {
      cameraCheckId: dialog,
      status: CameraCheckStatus.Compliant,
    };
    setCameraCheckStatus({ variables });
  };

  const onEnterKeyDownHandler = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    if (formRef.current) formRef.current.requestSubmit();
  };

  const onSubmit = async (data: IChatForm) => {
    await sendMessage({
      variables: {
        cameraCheckId: dialog,
        comment: data.message,
      },
    });
  };

  // boolean constants
  const profileDataLoading = profileLoading || !profile;
  const chatDataLoading = getCameraCheckState.loading || !chatData;

  useEffect(() => {
    if (dialog) getDialog();
    return () => (getCameraCheckState.data = undefined);
  }, [dialog]);

  useEffect(() => {
    if (getCameraCheckState.data) {
      setChatData(get(getCameraCheckState.data, "CameraGetter.getCameraCheck"));
    }
  }, [getCameraCheckState.data]);

  // Preloader
  if (profileDataLoading || !chatData) return <Preloader />;

  // boolean constant
  const checkList = !!chatData && !!chatData.comments;

  return (
    <div className={s.chatContainer}>
      {chatData.status !== CameraCheckStatus.Compliant &&
        userCanManageCamera && (
          <Button
            onClick={setStatusHandler(dialog)}
            txtBtn={"Согласовать"}
            customClassName={s.subModalBtn}
          />
        )}
      <div className={s.messageContainer}>
        {chatDataLoading && !checkList ? (
          <Preloader />
        ) : (
          chatData.comments.map(
            ({ comment, createdAt, createdBy, fullName, id }) => (
              <MessageItem
                key={id}
                fullName={fullName}
                comment={comment}
                createdAt={createdAt}
                isOutgoing={Number(profile.id) === Number(createdBy)}
              />
            )
          )
        )}
        {isSubmitting && <div></div>}
      </div>
      <form
        ref={formRef}
        onSubmit={handleSubmit(onSubmit)}
        className={s.chatUserActions}
      >
        {chatData.status !== CameraCheckStatus.Compliant && (
          <>
            <TextArea
              name={"message"}
              control={control}
              customClassName={s.textarea}
              onEnterKeyDown={onEnterKeyDownHandler}
              error={errors["message"]?.message}
              disabled={
                sendMessageState.loading || setCameraCheckStatusState.loading
              }
            />
            <Button
              isForm
              customClassName={s.roundButton}
              disabled={
                sendMessageState.loading || setCameraCheckStatusState.loading
              }
            >
              <Add />
            </Button>
          </>
        )}
      </form>
    </div>
  );
};

export default CameraCheckChat;

interface IMessageItemProps {
  isOutgoing: boolean;
  fullName: string;
  createdAt: string;
  comment: string;
}

const MessageItem = ({
  isOutgoing,
  fullName,
  createdAt,
  comment,
}: IMessageItemProps) => {
  return (
    <div
      className={clsx(s.messageBody, {
        [s.outgoingMessage]: isOutgoing,
      })}
    >
      <div className={s.userName}>
        <span>{getShortUserName(fullName)}</span>
        <span>{getFormatDate(createdAt)}</span>
      </div>
      <div className={s.message}>{comment}</div>
    </div>
  );
};
